import { useEffect, useRef, useSyncExternalStore } from "react";
import { fetchHeldDraft, type CheckoutOutcome } from "../../lib/api";
import { loadAnonKey, loadHeldKnock, loadPendingKnock } from "../../lib/draft";
import { ReopenDoorSheet } from "../you/reopen";
import { BillingPanel } from "./billing-panel";
import { CelebrateOverlay } from "./celebrate";
import { DataRequestsNotice } from "./data-requests";
import { ChatPane } from "./chat-pane";
import { useInboxDesk } from "./desk";
import { NewKnockSheet } from "./knock-sheet";
import { PaywallDialog } from "./paywall-dialog";
import { RightRail } from "./right-rail";
import { SettingsPanel } from "./settings-panel";
import { ShareDoorSheet } from "./share-sheet";
import { Sidebar } from "./sidebar";
import { DESKTOP, ShellProvider, isDesktop, useShellUi, type Overlay } from "./state";
import { useShellWorld } from "./use-conversations";
import { useR5a } from "./use-r5a";

/**
 * The whole signed-in product: a list, a conversation, and a desk.
 *
 * WHY THIS REPLACED A RAIL OF ROOMS. The old shell answered "where am I" and
 * left "what is going on" to whichever room you happened to be standing in —
 * which is why Home grew a console summarising four of them. A messenger's
 * geometry answers both at once: the list IS the state of the door, permanently
 * on screen, and everything else is a conversation inside it or a panel over
 * it. The console is gone because the thing it summarised is now the furniture.
 *
 * PANES ARE CSS, NOT A RESIZE LIBRARY. The owner's prototype uses draggable
 * splitters; three fixed columns give the same reading and cost nothing —
 * neither a dependency nor the per-viewer layout state a splitter has to
 * persist somewhere.
 */
export function AppShell({
  initialOverlay,
  paid,
  composeAt,
  onSetUpDoor,
}: {
  initialOverlay?: Overlay;
  paid?: CheckoutOutcome;
  /**
   * The door this visit came for — `/@maya`, or the target a sign-in or a
   * checkout carried home (owner, 2026-08-26). The shell opens a knock at it
   * instead of the poster, which is the whole of that ruling: the at-door flow
   * is for people who do not have an account yet.
   *
   * An initial value, like `initialOverlay` and for the same reason: the person
   * is then free to close the thread, and a controlled prop would spring it
   * straight back open on the next render.
   */
  composeAt?: string | null;
  /**
   * The door step, still owed (§2.3.3). Passed down rather than owned here
   * because the step is a SCREEN at `/`, not a panel over the shell: a knocker
   * hanging their first door gets the same three questions an owner did, with
   * the same counter above them, and a panel would be a second version of it.
   */
  onSetUpDoor?: (() => void) | undefined;
}) {
  return (
    <ShellProvider initialOverlay={initialOverlay} onSetUpDoor={onSetUpDoor ?? null}>
      <Shell paid={paid} composeAt={composeAt ?? null} />
    </ShellProvider>
  );
}

/**
 * The breakpoint, watched. The question itself is `isDesktop` in `state.tsx`,
 * which is also what seeds the desk's default — one definition, so the layout
 * and the seed cannot drift apart.
 */
function useDesktop(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const query = window.matchMedia(DESKTOP);
      query.addEventListener("change", onChange);
      return () => query.removeEventListener("change", onChange);
    },
    isDesktop,
    // The server has no viewport. Desktop is the safer guess: it renders both
    // panes, so nothing is missing before the first measurement.
    () => true,
  );
}

function Shell({ paid, composeAt }: { paid?: CheckoutOutcome; composeAt: string | null }) {
  const ui = useShellUi();
  const world = useShellWorld();
  const desk = useInboxDesk(ui.activeId ? world.byId(ui.activeId) : undefined);
  const desktop = useDesktop();

  useShortcuts();
  // R5a (ruling C7): the console is primary only for as long as owners are not
  // living in it. Measured from here because this is the one component that
  // sees both halves — that a session happened, and what got opened in it.
  useR5a({ hasDoor: world.hasDoor, active: ui.activeId ? world.byId(ui.activeId) : undefined });

  /**
   * WHERE THE SHELL OPENS — two answers, and this ref is which of them has been
   * given. Once only, either way: a person who closes what they were shown has
   * closed it, and neither effect below may put it back.
   */
  const landed = useRef(false);

  /**
   * The door this visit came for, opened as a conversation (owner, 2026-08-26)
   * — and before the landing below, because somebody who typed `/@maya` did not
   * come for their own briefing.
   *
   * THE TARGET IS NOT SPENT HERE, and that is deliberate. A reload mid-hearing
   * has to land back in the hearing: the door page kept the target for exactly
   * that reason ("a reload mid-grilling must land back at this door"), and the
   * address that used to do the re-aiming is now `/`, which would otherwise
   * answer with the briefing. So the key survives until the person CLOSES the
   * thread, and `clearCompose` is what closes it.
   */
  const aimed = useRef(false);
  useEffect(() => {
    if (aimed.current || !composeAt) return;
    aimed.current = true;
    // This visit has landed, and it landed HERE. Both effects run in the same
    // commit off the same stale `activeId`, so the landing below cannot be left
    // to notice the aim by reading it — it would read `null` and preselect the
    // owner's thread over the top of the door somebody came for.
    landed.current = true;
    ui.openCompose(composeAt);

    // A knock already open at this door: the thread is the HEARING, not a
    // draft, and it reads itself back from the server exactly as re-entry from
    // the list does. The same `pending knock` the door page resumed from.
    const pending = loadPendingKnock();
    if (pending && pending.handle === composeAt) {
      ui.composeStarted(pending.id);
      return;
    }

    // Otherwise: the words written BEFORE there was an account
    // (`pre-auth.tsx`), back in their hands at the first moment there is a box
    // to put them in. The stash itself is not cleared here — the send clears
    // it, exactly as it did at the door, so a reload before sending still
    // finds them.
    const held = loadHeldKnock();
    if (held && held.handle === composeAt && held.body.trim()) {
      ui.setComposeDraft(held.body);
      return;
    }

    // THE LOCAL COPY IS EMPTY, SO ASK THE SERVER (ruling R-0828-3).
    //
    // `pre-auth.tsx` writes the pre-account draft twice — once to this
    // browser's `localStorage` and once to the server under the anonymous key
    // — precisely so the words survive a browser that lost its storage, a
    // second tab, or a sign-in that came back somewhere else. Only the local
    // copy was ever read back, which made the server copy a write to nowhere
    // and the promise ("Nob did not lose what you wrote") true only on the
    // easy path.
    //
    // LOCAL STAYS PRIMARY. It is the copy that needs no network and cannot be
    // late, so it wins whenever it has anything; this runs only when it has
    // nothing at all. A `null` answer — no key, no row, expired, or a server
    // having a bad minute — is a knock that starts empty, exactly as before.
    //
    // NOTHING CANCELS THIS, and the absence is deliberate. `ui` is in this
    // effect's dependencies and its identity changes on every shell state
    // change — including the `openCompose` two lines up — so a cleanup that
    // abandoned the request would abandon it before it could ever land. The
    // late answer is guarded where lateness is actually decidable instead:
    // `seedComposeDraft` takes it only into a thread still at this handle,
    // still not a hearing, and still empty, so a person who closed the thread,
    // typed, or pressed send in the meantime is left exactly as they are.
    const key = loadAnonKey();
    if (!key) return;
    void fetchHeldDraft({ anon_key: key, door_handle: composeAt }).then((body) => {
      if (body) ui.seedComposeDraft(composeAt, body);
    });
  }, [composeAt, ui]);

  /**
   * Otherwise the shell opens on the owner's own doorman.
   *
   * It is the one thread that is always theirs, it carries the briefing, and it
   * is where a first visit's onboarding happens — so an empty middle pane on
   * arrival would be the product asking somebody to choose before it had said
   * anything.
   */
  useEffect(() => {
    if (landed.current || ui.activeId || !world.own) return;
    // …unless the door step is still owed. There is no doorman conversation to
    // land in yet — the thread belongs to a door that has not been hung — and
    // opening an empty pane would answer the pinned row's own question with
    // silence. The row asks; the list is what a knocker came back for.
    if (world.owesDoorStep) return;
    landed.current = true;
    ui.preselect(world.own.id);
  }, [ui, world.own, world.owesDoorStep]);

  /**
   * On a phone the desk is a LAYER over the conversation, so one left out when
   * the shell empties is the first thing back on screen next time somebody
   * opens a knock — a stale verdict, over a knock it was never about. Closing
   * it as the shell empties is what stops that.
   *
   * A desk needs no such reset: the rail is a column that is simply not
   * rendered while nothing is open, and out is where it is meant to be.
   */
  useEffect(() => {
    if (!desktop && !ui.activeId && ui.rightOpen) ui.setRightOpen(false);
  }, [desktop, ui]);

  const sidebar = <Sidebar world={world} />;
  const chat = <ChatPane world={world} desk={desk} />;
  const rail = (
    <RightRail
      world={world}
      atDoor={desk.knock}
      panel={desk.panel}
      busy={desk.busy}
      onAccept={() => {
        desk.accept();
        // On a phone the desk is over the composer it just opened.
        if (!desktop) ui.setRightOpen(false);
      }}
      onDecline={() => {
        desk.decline();
        if (!desktop) ui.setRightOpen(false);
      }}
    />
  );

  return (
    <div className="flex h-dvh min-h-0 flex-col bg-bg text-text">
      {/* An erasure or an export somebody asked us for (wave 116): the one
          line of the product that sits above everything, and only when there
          is something to confirm or download. */}
      <DataRequestsNotice />
      <div className="flex min-h-0 flex-1">
        {desktop ? (
          <div className="flex h-full w-full min-w-0">
            <div className="h-full w-[22rem] shrink-0">{sidebar}</div>
            <div className="h-full min-w-0 flex-1">{chat}</div>
            {ui.rightOpen && ui.activeId && (
              <div className="h-full w-[21.25rem] shrink-0">{rail}</div>
            )}
          </div>
        ) : (
          <div className="nob-stage flex w-full">
            <div
              className="nob-stage-pane nob-stage-list"
              data-on={String(ui.mobilePane === "list")}
            >
              {sidebar}
            </div>
            <div
              className="nob-stage-pane nob-stage-chat"
              data-on={String(ui.mobilePane === "chat")}
            >
              {chat}
            </div>
            {ui.rightOpen && ui.activeId && ui.mobilePane === "chat" && (
              <div className="nob-stage-desk">{rail}</div>
            )}
          </div>
        )}
      </div>

      {ui.overlay.kind === "settings" && (
        <SettingsPanel page={ui.overlay.page} focus={ui.overlay.focus} />
      )}
      {ui.overlay.kind === "billing" && (
        <BillingPanel view={ui.overlay.view} focus={ui.overlay.focus} paid={paid} />
      )}
      {ui.sheet === "share" && world.handle && (
        <ShareDoorSheet handle={world.handle} name={world.me?.displayName ?? ""} />
      )}
      {ui.sheet === "knock" && <NewKnockSheet />}
      {ui.sheet === "reopen" && <ReopenDoorSheet />}
      {ui.paywall && <PaywallDialog />}
      <CelebrateOverlay />
    </div>
  );
}

/**
 * Four keys, and the rule that decides when each one is the shell's business.
 *
 * ⌘K and ⌘N are chords nothing else in a browser claims, so they are taken
 * whatever is focused. "/" is a printable character and belongs to whatever
 * field has focus — it only reaches the composer from outside one. Escape
 * cascades through the overlay stack (`state.tsx`) and hands itself back to the
 * browser when the shell has nothing of its own to close.
 */
function useShortcuts(): void {
  const ui = useShellUi();
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing =
        target !== null &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      const meta = event.metaKey || event.ctrlKey;

      if (meta && event.key.toLowerCase() === "k") {
        event.preventDefault();
        document.querySelector<HTMLInputElement>("[data-shell-search]")?.focus();
        return;
      }
      if (meta && event.key.toLowerCase() === "n") {
        event.preventDefault();
        ui.openSheet("knock");
        return;
      }
      if (event.key === "/" && !typing) {
        const composer = document.querySelector<HTMLTextAreaElement>('[data-pane="chat"] textarea');
        if (!composer) return;
        event.preventDefault();
        composer.focus();
        return;
      }
      if (event.key === "Escape") {
        if (ui.escape()) event.preventDefault();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ui]);
}
